import * as React from 'react'
import {
  useGetPlantEntityConnectionsQuery,
  type PlantAdditionalProperty,
  type PlantEntityMeta
} from '@axilon-apps/domains-ontology-ui-state'
import { SourceDocViewer, useSourceDocViewer } from '../../../components/SourceDocViewer'
import { cn } from '../../../lib/utils'
import { CollapsibleContent } from '../CollapsibleContent'
import { SessionEntityPropertyChanges } from '../session-property-changes'
import { ConfidenceValue } from './ConfidenceValue'
import { ConnectionsPanel } from './ConnectionsPanel'
import { EntityProvenancePanel } from './EntityProvenancePanel'
import { InspectorHeading } from './InspectorHeading'

interface EntityDetailsInspectorProps {
  entity: PlantEntityMeta & { id: string }
  additionalProperties?: PlantAdditionalProperty[]
}

/**
 * Shared Plant detail footer: a page-level Source and evidence section when
 * provenance exists, plus a collapsed debug drawer for status / extra props /
 * connections. One mount covers every entity detail page.
 */
export function EntityDetailsInspector({
  entity,
  additionalProperties = []
}: EntityDetailsInspectorProps): React.ReactElement {
  const [expanded, setExpanded] = React.useState(false)
  const { openSource, viewerProps } = useSourceDocViewer()
  const connections = useGetPlantEntityConnectionsQuery({ id: entity.id })
  const groups = connections.data ?? []
  const validity =
    entity.valid === false
      ? { label: 'Invalidated', className: 'text-destructive' }
      : entity.valid === true
        ? { label: 'Re-validated', className: 'text-st-run' }
        : { label: 'Trusted', className: 'text-ink-3' }

  return (
    <div className="flex min-w-0 flex-col gap-card">
      <EntityProvenancePanel
        entityId={entity.id}
        provenance={entity.provenance}
        jobId={entity.lastIngestJobId}
        onOpenSource={openSource}
      />

      <SessionEntityPropertyChanges entityId={entity.id} />

      <section>
        <div className="mb-2 flex flex-wrap items-baseline gap-3">
          <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">
            Entity details
          </h3>
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
            className="m-0 border-0 bg-transparent p-0 text-12 font-medium text-brand hover:underline"
          >
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        </div>

        <CollapsibleContent open={expanded}>
          <div className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow-sm">
            <div className="divide-y px-3">
              <section className="py-2.5">
                <InspectorHeading label="Status" />
                <div className="mt-1.5 flex items-center gap-4">
                  <span className={cn('text-12', validity.className)}>{validity.label}</span>
                  <ConfidenceValue value={entity.confidence ?? entity.provenance?.confidence} />
                </div>
              </section>
              <AdditionalPropertiesDetails properties={additionalProperties} />
              <ConnectionsPanel
                entityId={entity.id}
                groups={groups}
                isLoading={connections.isLoading}
                jobId={entity.lastIngestJobId}
                onOpenSource={openSource}
              />
            </div>
          </div>
        </CollapsibleContent>
      </section>

      <SourceDocViewer {...viewerProps} />
    </div>
  )
}

function AdditionalPropertiesDetails({
  properties
}: {
  properties: PlantAdditionalProperty[]
}): React.ReactElement | null {
  if (properties.length === 0) return null
  return (
    <section className="py-2.5">
      <InspectorHeading label="Additional properties" count={properties.length} />
      <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 xl:grid-cols-[repeat(auto-fill,minmax(10rem,1fr))]">
        {properties.map(({ key, value }) => (
          <div key={key} className="min-w-0">
            <div className="text-10 font-semibold uppercase tracking-[0.06em] text-ink-4">
              {key}
            </div>
            <div className="mt-0.5 break-words text-12 text-ink-2">{value}</div>
          </div>
        ))}
      </div>
    </section>
  )
}
